import type { SeoEntityType, SeoIssue } from '@nucleus-commerce/api-client/admin';

export interface IssueDuplicate {
  /** An entity type the backend knows, or the raw prefix when it is not one. */
  entityType: string;
  handle: string;
}

export interface GraphProblem {
  path: string;
  message: string;
}

/** What the issues table needs to say about one issue in plain words. */
export interface IssueDetailView {
  /** i18n key under `seo:detail`. */
  messageKey: string;
  values: Record<string, string | number>;
  duplicates: IssueDuplicate[];
  graphProblems: GraphProblem[];
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

function duplicatesOf(value: unknown): IssueDuplicate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (typeof entry !== 'string') return [];
    const at = entry.indexOf(':');
    return at === -1
      ? [{ entityType: '', handle: entry }]
      : [{ entityType: entry.slice(0, at), handle: entry.slice(at + 1) }];
  });
}

function graphProblemsOf(value: unknown): GraphProblem[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const { path, message } = entry as Record<string, unknown>;
    return [{ path: text(path), message: text(message) }];
  });
}

/**
 * `detail` is a loose record whose shape depends on the code (see the backend's SEO audit):
 * the offending title or description, the duplicates as `entityType:handle`, the graph problems,
 * word counts, a redirect target. Unknown or missing keys degrade to the generic sentence.
 */
export function readIssueDetail(issue: Pick<SeoIssue, 'code' | 'detail'>): IssueDetailView {
  const detail = issue.detail;
  const base: IssueDetailView = {
    messageKey: issue.code,
    values: {},
    duplicates: [],
    graphProblems: [],
  };
  switch (issue.code) {
    case 'title_missing':
    case 'description_missing':
    case 'orphan_product':
      return base;
    case 'title_too_long': {
      const title = text(detail.title);
      return { ...base, values: { title, length: title.length } };
    }
    case 'description_too_long': {
      const description = text(detail.description);
      return { ...base, values: { length: description.length } };
    }
    case 'duplicate_title':
      return {
        ...base,
        values: { title: text(detail.title) },
        duplicates: duplicatesOf(detail.duplicates),
      };
    case 'duplicate_description':
      return {
        ...base,
        values: { description: text(detail.description) },
        duplicates: duplicatesOf(detail.duplicates),
      };
    case 'image_alt_missing': {
      const ids = Array.isArray(detail.mediaIds) ? detail.mediaIds : [];
      return { ...base, values: { count: ids.length } };
    }
    case 'thin_description': {
      const words = count(detail.words);
      const minimum = count(detail.minimum);
      return words === null || minimum === null
        ? { ...base, messageKey: 'thin_description_generic' }
        : { ...base, values: { words, minimum } };
    }
    case 'jsonld_invalid':
      return { ...base, graphProblems: graphProblemsOf(detail.issues) };
    case 'offer_missing_shipping':
      return base;
    case 'redirect_chain':
    case 'redirect_loop':
    case 'redirect_to_404':
      return { ...base, values: { toPath: text(detail.toPath) } };
  }
}

const KNOWN_ENTITY_TYPES: ReadonlySet<string> = new Set<SeoEntityType>([
  'store_product',
  'collection',
  'page',
  'blog_post',
  'redirect',
  'store',
]);

export function isKnownEntityType(value: string): value is SeoEntityType {
  return KNOWN_ENTITY_TYPES.has(value);
}
